/**
 * Add/Remove a class when an element enters/exits the viewport. Calling this function
 * automatically sets up any valid elements which have the relevent selector attributes.
 *
 * All elements with the attribute "data-jm-classonscroll" are added, and the class added is the value of
 * this attribute.
 *
 * Additional attributes and values:
 *      "data-jm-classonscroll-body"    - Boolean   If true, attach class to body instead of element.
 *      "data-jm-classonscroll-latch"   - Boolean   If true, the element should retain the added class after it is first
 *                                                  added, instead of being removed when off screen.
 */

import isInView from './isinview';

// getClassToAdd :: Element -> String
function getClassToAdd(element) {
    const classToAdd = element.dataset.jmClassonscroll || 'scrolled-to';
    return classToAdd;
}

// getTarget :: Element a -> Element b
function getTarget(element) {
    const isBody = element.dataset.jmClassonscrollBody === 'true';
    return isBody ? document.body : element;
}

// shouldLatch :: Element -> Boolean
function shouldLatch(element) {
    return element.dataset.jmClassonscrollLatch === 'true';
}

// notVisibleCallback :: Element -> Boolean
function notVisibleCallback(element) {
    const target = getTarget(element);
    const classToAdd = getClassToAdd(element);
    target.classList.remove(classToAdd);
    return false;
}

// visibleCallback :: Element -> Boolean
function visibleCallback(element) {
    const target = getTarget(element);
    const classToAdd = getClassToAdd(element);
    target.classList.add(classToAdd);
    return shouldLatch(element);
}

// setupIsInView :: Number -> [Element] -> Boolean
function setupIsInView(debounce, elements) {
    return isInView(elements, visibleCallback, notVisibleCallback, debounce);
}

// init :: Number -> Boolean
function init(debounce = 100) {
    const elements = [].slice.call(document.querySelectorAll('[data-jm-classonscroll]'));
    return setupIsInView(debounce, elements);
}

export { init };
export default { init };
